import { fas } from '@fortawesome/pro-solid-svg-icons';
import { createSlice } from '@reduxjs/toolkit';

// utils
import api from "../api";
import { singleFlight } from '../../utils/singleFlight';
import { preparePostalRequest } from '../../utils/kingstonPostalSearch';
import { assertKingstonPostalResponse } from '../../utils/kingstonPostal.mjs';
//
import { dispatch } from '../store';
const readProperty = singleFlight(id => api.getById('/properties', id, true));

// ----------------------------------------------------------------------

const initialState = {
	relatedProperties: [],
	properties: null,
  prevProperties: null,
	mapList: null,
  // Every listing matching the current filters, as [id, lng, lat, price,
  // currency] tuples. Clustering happens in the browser from these, so they are
  // fetched once per filter change rather than on every pan and zoom.
  mapPoints: null,
  loadingMapPoints: false,
	cluster: null,
  prevCluster: null,
	point: null,
	nearby: null,
	likedProperties: [],
	viewedProperties: [],
	property: null,
	error: null,
	searchKeyword: '',
	orderColumn: null,
	loading: false,
	loadingMap: false,
  mapListError: false,
  propertiesParams: null,
	stats: null,
  isCluster: false,
  sliderProperty: null,
  isSearching: false,
};

const slice = createSlice({
  name: 'property',
  initialState,
  reducers: {
    // START LOADING
    startLoading(state) {
      state.loading = true;
    },
    startListingsLoading(state, action) {
      state.propertiesParams = action.payload;
      state.loading = true;
      state.error = null;
      state.properties = null;
    },
    startPropertyLoading(state) {
      state.loading = true;
      state.property = null;
      state.error = null;
    },

     // START LOADING
    startMapLoading(state) {
      state.loadingMap = true;
      state.mapListError = false;
    },

    // HAS ERROR
    hasError(state, action) {
      state.loading = false;
      state.error = action.payload;
    },

    // GET BUDGETS
    getPropertiesSuccess(state, action) {
      state.loading = false;
      console.log(action.payload, 'action.payload');
      state.properties = action.payload;
    },

    // GET PROPERTY
    getPropertySuccess(state, action) {
      state.loading = false;
      state.property = action.payload;
    },

    // UPDATE PROPERTY
    updatePropertySuccess(state, action) {
      state.loading = false;
      state.property = action.payload;
    },

    // GET PROPERTY STATS
    getPropertyStatsSuccess(state, action) {
      state.loading = false;
      state.stats = action.payload;
    },

    // GET LIKED PROPERTIES
    getLikedPropertiesSuccess(state, action) {
      state.loading = false;
      state.likedProperties = action.payload;
    },

    // GET VIEWED PROPERTIES
    getViewedPropertiesSuccess(state, action) {
      state.loading = false;
      state.viewedProperties = action.payload;
    },

    // GET POINT ON MAP PROPERTIES
    getMapPointSuccess(state, action) {
      state.loading = false;
      state.point = action.payload;
    },

    // GET RELATED PROPERTIES
    getRelatedPropertiesSuccess(state, action) {
      state.loading = false;
      state.relatedProperties = action.payload;
    },

    // GET PROPERTIES NEARBY
    getMapNearbyPropertiesSuccess(state, action) {
      state.loading = false;
      state.nearby = action.payload;
    },

    // GET PROPERTIES OF A CLUSTER
    getMapClusterSuccess(state, action) {
      state.loading = false;
      state.prevCluster = state.cluster;
      state.cluster = action.payload;
    },

    // MAP POINTS (for client-side clustering)
    startMapPointsLoading(state) {
      state.loadingMapPoints = true;
      state.mapPoints = null;
    },

    getMapPointsSuccess(state, action) {
      state.loadingMapPoints = false;
      state.mapPoints = action.payload;
    },

    getMapPointsFailure(state, action) {
      state.loadingMapPoints = false;
      state.mapPoints = null;
      state.error = action.payload;
    },

    getMapListFailure(state) {
      state.loading = false;
      state.loadingMap = false;
      state.mapListError = true;
      state.properties = null;
      state.mapList = null;
    },

    // LIST PROPERTIES
    getMapListSuccess(state, action) {
      state.loading = false;
      state.loadingMap = false;
      state.mapListError = false;
      state.mapList = action.payload;
      state.properties = action.payload.results;
    },

    // SET SELECTED PROPERTY
    setSliderProperty(state, action) {
      const { property, isCluster } = action.payload;
      state.sliderProperty = property;
      state.isCluster = isCluster;
    },

    setSeachingListings(state, action) {
      state.isSearching = action.payload;
    },
  },
});

// Reducer
export default slice.reducer;

// ----------------------------------------------------------------------

let listRequest = 0;
export function getProperties(params) {
  return async () => {
    const request = ++listRequest;
    dispatch(slice.actions.startListingsLoading(params));
    try {
      const safeParams = await preparePostalRequest(api, params);
      const { data } = await api.get('/properties', safeParams);
      if (safeParams.postal_district) assertKingstonPostalResponse(data, safeParams.postal_district);
      if (request !== listRequest) return;
      dispatch(slice.actions.getPropertiesSuccess(data));
    } catch (error) {
      if (request !== listRequest) return;
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getMapList(params) {
  return async () => {
    const request = ++listRequest;
    dispatch(slice.actions.startListingsLoading(params));
    dispatch(slice.actions.startMapLoading());
    try {
      const safeParams = await preparePostalRequest(api, params);
      const { data } = await api.get('/properties/map', safeParams);
      if (safeParams.postal_district) assertKingstonPostalResponse(data, safeParams.postal_district);
      if (request !== listRequest) return;
      dispatch(slice.actions.getMapListSuccess(data));
    } catch (error) {
      if (request !== listRequest) return;
      dispatch(slice.actions.getMapListFailure());
    }
  };
}

// ----------------------------------------------------------------------

/**
 * Fetch every listing matching the filters as lean points, for clustering in
 * the browser. Deliberately not called on pan or zoom — only when the filters
 * that define the set change.
 */
let pointsRequest = 0;
export function getMapPoints(params) {
  return async () => {
    const request = ++pointsRequest;
    dispatch(slice.actions.startMapPointsLoading());
    try {
      const safeParams = await preparePostalRequest(api, params);
      const { data } = await api.get('/properties/map/points', safeParams);
      if (safeParams.postal_district) assertKingstonPostalResponse(data, safeParams.postal_district);
      if (request !== pointsRequest) return;
      dispatch(slice.actions.getMapPointsSuccess(data));
    } catch (error) {
      if (request !== pointsRequest) return;
      dispatch(slice.actions.getMapPointsFailure(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getCluster(payload) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const { data } = await api.post('/properties/cluster', payload);
      dispatch(slice.actions.getMapClusterSuccess({
        results: data.response,
      }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getRelatedProperties(params) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const { data } = await api.get('/properties', params);
      dispatch(slice.actions.getRelatedPropertiesSuccess(data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getNearby(params) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const { data } = await api.get('/properties/near', params);
      dispatch(slice.actions.getMapNearbyPropertiesSuccess(data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}


// ----------------------------------------------------------------------

export function updateProperty(params) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const { data } = await api.put('/properties', params.id, params.payload, true);
      dispatch(slice.actions.updatePropertySuccess(data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

let propertyRequest = 0;
export function getProperty(params) {
  return async () => {
    const request = ++propertyRequest;
    dispatch(slice.actions.startPropertyLoading());
    try {
      const { data } = await readProperty(params);
      if (request !== propertyRequest) return;
      const propertyData = data?.response ?? data;
      if (!propertyData) {
        dispatch(slice.actions.hasError(new Error('Invalid property data')));
        return;
      }
      if (propertyData.photos) {
        try {
          const images = typeof propertyData.photos === 'string'
            ? JSON.parse(propertyData.photos)
            : propertyData.photos;
          const photos = (Array.isArray(images) ? images : []).map((p) => {
            if (p?.headerInfo?.location) {
              return {
                img: p.headerInfo.location,
                id: p.headerInfo.contentId
              };
            }
            if (p?.img) return p;
            if (typeof p === 'string') return { img: p };
            return null;
          });
          propertyData.photos = JSON.stringify(photos.filter(Boolean));
        } catch (e) {
          // Keep original photos if parsing fails
        }
      }
      dispatch(slice.actions.getPropertySuccess(propertyData));
    } catch (error) {
      if (request !== propertyRequest) return;
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getPoint(id) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const { data } = await api.get(`/properties/${id}`);
      dispatch(slice.actions.getMapPointSuccess(data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getViewedProperties(params) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const { data } = await api.get('/buyers/viewed-properties', {}, true);
      dispatch(slice.actions.getViewedPropertiesSuccess(data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function getPropertyStats(params) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      const { data } = await api.get('/properties/stats', {}, true);
      dispatch(slice.actions.getPropertyStatsSuccess(data));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function selectSliderProperty(property, isCluster = false) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      dispatch(slice.actions.setSliderProperty({
        property,
        isCluster,
      }));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

// ----------------------------------------------------------------------

export function onSearchListings(isSearching) {
  return async () => {
    dispatch(slice.actions.startLoading());
    try {
      dispatch(slice.actions.setSeachingListings(isSearching));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
